/**
 * MINI_FIX_BT — normalize a backend-emitted English account name (`accountEn`)
 * into the i18n key suffix used by `je.explainer.*` / `je.explainer.*.std`.
 *
 * Pure, React-free, format-free, side-effect-free — keep it that way so the
 * Yevmiye explainer popover stays robust against new account variants and so
 * a tiny unit test can pin the mapping table.
 *
 * Why a normalization layer at all: a few accounts have per-asset-class
 * variants in `accountEn` ("Right-of-Use Asset — Buildings",
 * "Accumulated Depreciation — Vehicles", "Interest Expense — Lease", …).
 * The explainer text is the SAME regardless of class / suffix — only the
 * amount changes — so we collapse the variants to a single canonical key.
 *
 * Unknown / unmapped account names fall back to `"generic"`. The popover
 * MUST never crash, blank out, or echo raw English on a new backend
 * account; the generic key carries a safe IFRS 16 catch-all.
 */

export function accountExplainerKey(accountEn: string): string {
  if (!accountEn) return "generic";

  // ── Per-class variants (single key collapses all suffixes) ──────────────
  if (accountEn.startsWith("Right-of-Use Asset")) return "rouAsset";
  if (accountEn.startsWith("Accumulated Depreciation")) return "accDep";
  if (accountEn.startsWith("Depreciation Expense")) return "depreciationExpense";
  if (accountEn.startsWith("Interest Expense")) return "interestExpense";
  if (accountEn.startsWith("Variable Lease Expense")) return "variableExpense";

  // ── Lease liability (exact + ST/LT split) ───────────────────────────────
  if (accountEn === "Lease Liability") return "leaseLiability";
  if (accountEn === "Lease Liability — Short-Term") return "leaseLiabilityST";
  if (accountEn === "Lease Liability — Long-Term") return "leaseLiabilityLT";

  // ── Cash variants (generic, termination, restoration, prepayment) ───────
  if (accountEn === "Cash — Termination Penalty") return "terminationPenalty";
  if (accountEn === "Cash — Restoration Costs") return "restorationCosts";
  if (accountEn === "Cash — Lease Prepayment at Commencement") {
    return "leasePrepayment";
  }
  if (
    accountEn === "Cash / Bank" ||
    accountEn === "Cash" ||
    accountEn === "Bank"
  ) {
    return "cash";
  }

  // ── FX P&L ──────────────────────────────────────────────────────────────
  if (accountEn === "FX Loss" || accountEn === "FX Loss — Lease") return "fxLoss";
  if (accountEn === "FX Gain" || accountEn === "FX Gain — Lease") return "fxGain";

  // ── Equity ──────────────────────────────────────────────────────────────
  if (accountEn === "Retained Earnings") return "retainedEarnings";

  // ── Modification / termination gains + losses ───────────────────────────
  if (accountEn === "Gain on Lease Modification") return "gainModification";
  if (accountEn === "Gain on Lease Termination") return "gainTermination";
  if (
    accountEn === "Loss on Lease Modification" ||
    accountEn === "Loss on Lease Termination"
  ) {
    return "lossModTerm";
  }

  // ── Index remeasurement gain (when negative remeasurement floors at 0) ──
  if (accountEn === "Gain on Index Remeasurement") return "indexGain";

  // ── Other balance-sheet items ───────────────────────────────────────────
  if (accountEn === "Restoration Provision") return "restorationProvision";
  if (accountEn === "Lease Incentive Liability") return "leaseIncentive";
  if (accountEn === "Prepaid Lease Payments") return "prepaidLease";

  return "generic";
}
